import React, { useRef, createContext, useContext, useMemo } from 'react';
import { debounce } from 'lodash-es';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';

interface AccommodationMapListSyncContextValue {
  hoveredHotelId: string | null;
  onHoverStart: (hotelId: string) => void;
  onHoverEnd: () => void;
}

const AccommodationMapListSyncContext = createContext<AccommodationMapListSyncContextValue | null>(null);

export const useAccommodationMapListSync = () => {
  const context = useContext(AccommodationMapListSyncContext);
  if (!context) {
    throw new Error('useAccommodationMapListSync must be used within AccommodationMapListSync');
  }
  return context;
};

interface AccommodationMapListSyncProps {
  children: React.ReactNode;
}

/**
 * This component is used to sync hotel id/events/actions between the map and the list.
 * To sync actionable events between the map and the list.
 *
 * Example:
 * - When the user hovers over a hotel on the map, the hotel id is synced to the list.
 * - When the user clicks on a hotel on the map, the hotel id is synced to the list.
 * - When the user hovers over a hotel in the list, the hotel id is synced to the map.
 * - When the user clicks on a hotel in the list, the hotel id is synced to the map.
 *
 * This is used to ensure that the user sees the same hotel in both the map and the list.
 */
export const AccommodationMapListSync = React.memo<AccommodationMapListSyncProps>(({ children }) => {
  const hoveredHotelIdRef = useRef<string | null>(null);
  const [hoveredHotelId, setHoveredHotelId] = React.useState<string | null>(null);

  // Debounce hover start to reduce rapid state updates when moving mouse quickly across listings
  const debouncedSetHoveredHotelId = useMemo(
    () =>
      debounce((hotelId: string) => {
        hoveredHotelIdRef.current = hotelId;
        setHoveredHotelId(hotelId);
      }, 50),
    [],
  );

  const onHoverStart = useEventCallback((hotelId: string) => {
    // Skip if already hovering the same hotel
    if (hoveredHotelIdRef.current === hotelId) {
      return;
    }
    // Update ref immediately to prevent duplicate calls while debounce is pending
    hoveredHotelIdRef.current = hotelId;
    debouncedSetHoveredHotelId(hotelId);
  });

  const onHoverEnd = useEventCallback(() => {
    // Cancel any pending hover start to prevent stale updates
    debouncedSetHoveredHotelId.cancel();
    // Immediately clear on hover end for responsive feel
    if (hoveredHotelIdRef.current !== null) {
      hoveredHotelIdRef.current = null;
      setHoveredHotelId(null);
    }
  });

  const contextValue = useMemo(
    () => ({
      hoveredHotelId,
      onHoverStart,
      onHoverEnd,
    }),
    [hoveredHotelId, onHoverStart, onHoverEnd],
  );

  return <AccommodationMapListSyncContext.Provider value={contextValue}>{children}</AccommodationMapListSyncContext.Provider>;
});

AccommodationMapListSync.displayName = 'AccommodationMapListSync';
